import React, { useEffect, useState } from "react";
import { Tent, MapPin, Users, Plus } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { Modal, TextField, SelectField, TextArea } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";

function toLocalInput(d) { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; }

function CreateTailgateModal({ open, onClose, onCreated }) {
  const [games, setGames] = useState([]);
  const now = new Date();
  const [f, setF] = useState({ game_id: "", title: "", venue_label: "", description: "", audience: "public", capacity: 50, cell: "", start_time: toLocalInput(now), end_time: toLocalInput(new Date(now.getTime() + 3 * 3600000)) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  useEffect(() => { if (open) TurfTalk.games().then((r) => { const g = r.games || []; setGames(g); if (g[0]) setF((s) => ({ ...s, game_id: g[0].id })); }).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!f.game_id || f.title.length < 2 || f.venue_label.length < 2 || f.description.length < 2) { toast.error("Fill in game, title, venue and description."); return; }
    setBusy(true);
    try {
      const body = { game_id: f.game_id, title: f.title, venue_label: f.venue_label, description: f.description, audience: f.audience, start_time: new Date(f.start_time).toISOString(), end_time: new Date(f.end_time).toISOString(), capacity: Number(f.capacity) || null, cell: f.cell || null };
      await TurfTalk.tailgateCreate(body);
      toast.success("Tailgate created");
      onCreated?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create tailgate" testid="create-tailgate-modal">
      <SelectField label="Game" value={f.game_id} onChange={set("game_id")} data-testid="tg-game">
        {games.map((g) => <option key={g.id} value={g.id}>{`${g.away.abbr} @ ${g.home.abbr} · ${g.league} WK${g.week}`}</option>)}
      </SelectField>
      <TextField label="Title" value={f.title} onChange={set("title")} data-testid="tg-title" maxLength={80} />
      <TextField label="Venue" value={f.venue_label} onChange={set("venue_label")} data-testid="tg-venue" maxLength={80} />
      <TextArea label="Description" value={f.description} onChange={set("description")} data-testid="tg-desc" maxLength={1000} />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Start" type="datetime-local" value={f.start_time} onChange={set("start_time")} data-testid="tg-start" />
        <TextField label="End" type="datetime-local" value={f.end_time} onChange={set("end_time")} data-testid="tg-end" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Audience" value={f.audience} onChange={set("audience")} data-testid="tg-audience">
          <option value="public">Public</option><option value="connections">Connections</option><option value="invite_only">Invite only</option>
        </SelectField>
        <TextField label="Capacity" type="number" min={2} max={500} value={f.capacity} onChange={set("capacity")} data-testid="tg-capacity" />
      </div>
      <button onClick={submit} disabled={busy} data-testid="tg-create-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium disabled:opacity-50">{busy ? "Creating…" : "Create tailgate"}</button>
    </Modal>
  );
}

export default function TailgatesPage() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => TurfTalk.tailgateList({}).then((r) => setList(r.tailgates || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const join = async (id) => { try { await TurfTalk.tailgateJoin(id); toast.success("Joined tailgate"); load(); } catch (e) { toast.error(e.message); } };
  const leave = async (id) => { try { await TurfTalk.tailgateLeave(id); toast.success("Left tailgate"); load(); } catch (e) { toast.error(e.message); } };
  const cancel = async (id) => { try { await TurfTalk.tailgateCancel(id); toast.success("Tailgate cancelled"); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Tailgates" subtitle="Location-aware fan meetups and hangouts around the slate." right={
        <button onClick={() => setShowCreate(true)} data-testid="create-tailgate-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white text-sm font-medium"><Plus size={16} /> Create tailgate</button>
      } />
      {list === null ? <div className="grid sm:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        : list.length === 0 ? <Empty title="No tailgates yet" detail="Create the first tailgate for a game." icon={Tent} />
        : (
          <div className="grid sm:grid-cols-2 gap-4">
            {list.map((t) => {
              const mine = t.host_id === user?.id || t.owner_id === user?.id;
              const joined = t.is_member || t.joined;
              return (
                <div key={t.id} className="tt-panel rounded-xl p-4" data-testid={`tailgate-${t.id}`}>
                  <div className="font-display text-lg text-white">{t.title || t.name || "Tailgate"}</div>
                  <div className="text-white/50 text-sm mt-1 flex items-center gap-3 flex-wrap">
                    {(t.venue_label || t.cell) && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {t.venue_label || t.cell}</span>}
                    {t.member_count != null && <span className="inline-flex items-center gap-1"><Users size={13} /> {t.member_count}</span>}
                    <span className="text-[10px] px-1.5 rounded bg-white/10 uppercase">{t.audience}</span>
                  </div>
                  {t.description && <p className="text-white/60 text-sm mt-2 line-clamp-3">{t.description}</p>}
                  <div className="mt-3 flex gap-2">
                    {mine ? <button onClick={() => cancel(t.id)} data-testid={`tg-cancel-${t.id}`} className="px-3 h-8 rounded-lg tt-panel-3 text-white/80 text-xs">Cancel</button>
                      : joined ? <button onClick={() => leave(t.id)} data-testid={`tg-leave-${t.id}`} className="px-3 h-8 rounded-lg tt-panel-3 text-white/80 text-xs">Leave</button>
                      : <button onClick={() => join(t.id)} data-testid={`tg-join-${t.id}`} className="px-3 h-8 rounded-lg bg-[#2F7D32] text-white text-xs">Join</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <CreateTailgateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
    </div>
  );
}
